import React, { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useDevice } from '../providers/DeviceProvider.jsx';
import { useSession } from '../providers/SessionProvider.jsx';
import { useConsent } from '../providers/ConsentProvider.jsx';
import { AdSenseSafetyGate } from '../AdSenseSafetyGate.jsx';
import { VIM_MEDIA_LOGO_URL } from '../../services/media/mediaUrl.js';
import { loadProfile } from '../../services/account/accountService.js';
import { ContentRichnessAddon } from '../../pages/ContentRichness.jsx';
import '../../styles/mobile-more.css';
import '../../styles/cinematic-mockup.css';
import '../../styles/cinematic-catalog-variants.css';
import '../../styles/profile-navigation.css';

const primary = [
  ['/home', 'Home'],
  ['/movies', 'Movies'],
  ['/shows', 'Series'],
  ['/shorts', 'Shorts'],
  ['/originals', 'Originals'],
];

const business = [
  ['/subscribe', 'Subscribe'],
  ['/submit', 'Submit Film'],
  ['/filmmaker-services', 'Post-Production'],
  ['/filmmakers', 'Filmmakers'],
  ['/casting', 'Casting'],
  ['/careers', 'Careers'],
  ['/advertise', 'Advertise'],
];

const legal = [
  ['/about', 'About'],
  ['/press', 'Newsroom'],
  ['/faq', 'FAQ'],
  ['/privacy', 'Privacy'],
  ['/cookie-policy', 'Cookie Policy'],
  ['/refund-policy', 'Refund Policy'],
  ['/terms', 'Terms'],
  ['/dmca', 'DMCA'],
  ['/accessibility', 'Accessibility'],
  ['/support', 'Support'],
];

function Icon({ name }) {
  const common = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  if (name === 'Home') return <svg {...common}><path d="M3 11.5 12 4l9 7.5"/><path d="M5.5 10.5V20h13v-9.5"/><path d="M9.5 20v-5h5v5"/></svg>;
  if (name === 'Movies') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 9 4-4 4 4 4-4 4 4"/></svg>;
  if (name === 'Series') return <svg {...common}><rect x="4" y="5" width="16" height="14" rx="2"/><path d="m9 2 3 3 3-3"/><path d="M8 10h8M8 14h5"/></svg>;
  if (name === 'Shorts') return <svg {...common}><path d="M8 4h8l-3 6h4l-8 10 2-7H7l1-9Z"/></svg>;
  if (name === 'Originals') return <svg {...common}><path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3Z"/></svg>;
  if (name === 'Search') return <svg {...common}><circle cx="11" cy="11" r="6.5"/><path d="m16 16 4 4"/></svg>;
  if (name === 'Continue') return <svg {...common}><path d="M4.8 8.8A8 8 0 1 1 4 13"/><path d="M4 4v5h5"/><path d="m10 9 5 3-5 3V9Z"/></svg>;
  if (name === 'More') return <svg {...common}><circle cx="5" cy="12" r="1"/><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/></svg>;
  return <svg {...common}><circle cx="12" cy="8" r="3.5"/><path d="M5.5 20c.8-4.2 3-6.2 6.5-6.2s5.7 2 6.5 6.2"/></svg>;
}

function HeaderAccountIcon({ avatarUrl }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [avatarUrl]);

  return (
    <span className="action-icon account-action-icon" aria-hidden="true">
      {avatarUrl && !failed ? (
        <img
          className="account-nav-avatar"
          src={avatarUrl}
          alt=""
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <Icon name="Account" />
      )}
    </span>
  );
}

function NavigationLinks({ items, className = '', label = 'Primary navigation', onNavigate }) {
  return (
    <nav className={className} aria-label={label}>
      {items.map(([to, text]) => ['/filmmaker-services', '/press'].includes(to) ? (
        // Preserved and server-rendered documents need a full navigation.
        // Use a full navigation so React Router does not intercept it.
        <a key={to} href={to} onClick={onNavigate} className="nav-link">
          <span className="nav-icon"><Icon name={text} /></span>
          <span className="nav-label">{text}</span>
        </a>
      ) : (
        <NavLink key={to} to={to} onClick={onNavigate} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
          <span className="nav-icon"><Icon name={text} /></span>
          <span className="nav-label">{text}</span>
        </NavLink>
      ))}
    </nav>
  );
}

export function SiteLayout() {
  const { mode } = useDevice();
  const { ready, user } = useSession();
  const { openPreferences } = useConsent();
  const [moreOpen, setMoreOpen] = useState(false);
  const [profileAvatar, setProfileAvatar] = useState('');
  const userAvatar = String(user?.user_metadata?.avatar_url || user?.user_metadata?.avatarUrl || '').trim();
  const accountLabel = !ready ? 'Account' : user ? 'Profile' : 'Login';
  const accountAvatar = user ? (userAvatar || profileAvatar) : '';
  const closeMore = () => setMoreOpen(false);

  useEffect(() => {
    let active = true;

    if (!user?.id) {
      setProfileAvatar('');
      return () => { active = false; };
    }

    // Prefer the current Auth-session avatar immediately, then reconcile with
    // the profiles row so legacy/profile-only avatars still appear in the header.
    setProfileAvatar(userAvatar);
    loadProfile(user.id)
      .then((profile) => {
        if (!active) return;
        setProfileAvatar(String(profile?.avatar_url || userAvatar || '').trim());
      })
      .catch(() => {
        if (active) setProfileAvatar(userAvatar);
      });

    return () => { active = false; };
  }, [user?.id, userAvatar]);

  return (
    <div className="site-shell" data-device={mode} data-design="cinematic-mockup">
      <AdSenseSafetyGate />
      <header className="site-header">
        <NavLink className="brand" to="/home" aria-label="WatchVIM Home">
          <img src={VIM_MEDIA_LOGO_URL} alt="VIM Media" decoding="async" />
        </NavLink>
        <NavigationLinks items={primary} className="desktop-nav" />
        <div className="header-actions">
          <NavLink className="header-action" to="/continue-watching" aria-label="Continue Watching">
            <span className="action-icon"><Icon name="Continue" /></span>
            <span className="action-label">Continue</span>
          </NavLink>
          <NavLink className="header-action" to="/search" aria-label="Search">
            <span className="action-icon"><Icon name="Search" /></span>
            <span className="action-label">Search</span>
          </NavLink>
          <NavLink className="header-action account-header-action" to={user ? '/profile' : '/login'} aria-label={accountLabel}>
            <HeaderAccountIcon avatarUrl={accountAvatar} />
            <span className="action-label">{accountLabel}</span>
          </NavLink>
          <button
            type="button"
            className="header-action mobile-more-toggle"
            aria-label="More WatchVIM links"
            aria-expanded={moreOpen}
            aria-controls="mobile-more-menu"
            onClick={() => setMoreOpen((open) => !open)}
          >
            <span className="action-icon"><Icon name="More" /></span>
            <span className="action-label">More</span>
          </button>
        </div>
      </header>

      <main className="site-main">
        <Outlet />
        <ContentRichnessAddon />
      </main>

      <footer className="site-footer">
        <div className="parent-brand">
          <img src={VIM_MEDIA_LOGO_URL} alt="VIM Media" decoding="async" />
        </div>
        <NavigationLinks items={business} className="business-nav" label="Business navigation" />
        <div className="legal-links">
          {legal.map(([to, text]) => to === '/press'
            ? <a key={to} href={to}>{text}</a>
            : <NavLink key={to} to={to}>{text}</NavLink>)}
          <button type="button" className="cookie-settings-button" onClick={openPreferences}>Privacy &amp; Cookie Settings</button>
        </div>
      </footer>

      {moreOpen && (
        <div className="mobile-more-backdrop" onClick={closeMore}>
          <section
            id="mobile-more-menu"
            className="mobile-more-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-more-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-more-heading">
              <h2 id="mobile-more-title">More from WatchVIM</h2>
              <button type="button" className="mobile-more-close" onClick={closeMore} aria-label="Close More menu">×</button>
            </div>
            <NavigationLinks items={business} className="mobile-more-links" label="WatchVIM business links" onNavigate={closeMore} />
            <NavigationLinks items={legal} className="mobile-more-links mobile-more-legal" label="WatchVIM legal and support links" onNavigate={closeMore} />
            <button type="button" className="cookie-settings-button mobile-cookie-settings" onClick={() => { closeMore(); openPreferences(); }}>Privacy &amp; Cookie Settings</button>
          </section>
        </div>
      )}

      <NavigationLinks items={primary} className="mobile-bottom-nav" label="Mobile navigation" />
    </div>
  );
}
